import React from 'react';
import { Accordion, AccordionSummary, AccordionDetails, Typography, Box, Divider, Link } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {Helmet} from "react-helmet";

function faq() {
  // Define blue-colored company email and hotline link as JSX elements
  const companyEmailLink = (
    <Link href="mailto:support@paircare.ai" >
      support@paircare.ai
    </Link>
  );

  const hotlineLink = (
    <Link
      href="https://humantraffickinghotline.org/"
      target="_blank"
      rel="noopener"
    >
      https://humantraffickinghotline.org/
    </Link>
  );

  // Government hotline number remains as text.
  const governmentHotline = "1-888-373-7888"; // Example hotline

  // FAQ items for Service Providers (answers as JSX fragments)
  const serviceProviderFAQs = [
    {
      question: "Can I change my subscription type?",
      answer: <>Yes, you can change it at anytime.</>
    },
    {
      question: "What should I do if I have any technical issue while setting up my page on www.paircare.ai?",
      answer: <>Contact customer service or send us an email at {companyEmailLink}.</>
    },
    {
      question: "Can I issue coupons, promotions, or discounts to my potential clients?",
      answer: <>Yes, you should get our Gold subscription to be able to issue coupons, promotions, or discounts.</>
    },
    {
      question: "How can I utilize your payment system, calendar, and background check system?",
      answer: <>You should get our Platinum subscription to be able to utilize these features.</>
    },
    {
      question: "How can I report illegal activities by clients or any 3rd party?",
      answer: (
        <>
          If you suspect any illegal activities—including fraud, human trafficking, or other criminal behavior—please immediately contact your local law enforcement. Additionally, you can report incidents on our platform by sending an email to {companyEmailLink}. For more information on human trafficking, please call {governmentHotline} or visit {hotlineLink}.
        </>
      )
    },
    {
      question: "Can I cancel appointments of my clients?",
      answer: (
        <>
          You should directly contact your clients and fully refund any deposit they have paid. You should also reschedule the appointment with the client. Please note that our company does not assume liability for any potential conflicts between service providers and clients.
        </>
      )
    },
    {
      question: "What if I have payment issues with clients?",
      answer: (
        <>
          You should directly contact your clients or the payment system's provider. Please note that our company does not assume liability for any potential conflicts between service providers and clients.
        </>
      )
    }
  ];

  // FAQ items for Clients
  const clientFAQs = [
    {
      question: "Can I subscribe for your news?",
      answer: <>Yes, please send an email to {companyEmailLink} to subscribe for our news.</>
    },
    {
      question: "What should I do if I have any technical issue with my profile on www.paircare.ai?",
      answer: <>Contact customer service or send us an email at {companyEmailLink}.</>
    },
    {
      question: "Can I receive coupons, promotions, or discounts from potential service providers?",
      answer: <>Yes, please directly contact with the service providers.</>
    },
    {
      question: "Can I use your payment system to pay the service providers?",
      answer: <>Yes, our payment system is provided by a reliable third party.</>
    },
    {
      question: "How can I report illegal activities by a service provider or any third party?",
      answer: (
        <>
          If you suspect any illegal activities—including fraud or human trafficking—please immediately contact your local law enforcement. Additionally, you can report such incidents on our platform by sending an email to {companyEmailLink}. For further assistance, refer to the hotline at {governmentHotline} or visit {hotlineLink}.
        </>
      )
    },
    {
      question: "Can I cancel my booking?",
      answer: (
        <>
          Depending on the service provider's policy, you can cancel your booking with a full or partial refund. You should directly contact the service provider. If the issue is not resolved, please contact our customer service. Note that our company does not assume liability for any potential conflicts between service providers and clients.
        </>
      )
    },
    {
      question: "What if I have payment issues with a service provider?",
      answer: (
        <>
          You should directly contact the service provider or the payment's system provider. Please note that our company does not assume liability for any potential conflicts between service providers and clients.
        </>
      )
    }
  ];

  // Styling for questions to look like blue links
  const questionStyle = {
    color: '#0a66c2',
    cursor: 'pointer'
  };

  // Handle scroll-to-end when an accordion is expanded
  const handleAccordionChange = (id) => (event, expanded) => {
    if (expanded) {
      const element = document.getElementById(id);
      if (element) {
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offset = 60; // Your desired margin
  
        window.scrollTo({
          top: elementPosition - offset,
          behavior: 'smooth',
        });
      }
    }
  };

  return (
    <Box maxWidth={"700px"} margin={"auto"}>
      <Helmet>
        <title>PairCare - Frequently Asked Questions</title>
      </Helmet>
      <Box sx={{ margin: "20px" }}>
        <Typography variant="h4" gutterBottom align="center">
          Frequently Asked Questions
        </Typography>
        <Divider sx={{ marginBottom: 3 }} />

        {/* FAQ for Service Providers */}
        <Box sx={{ marginBottom: "20px" }}>
          <Typography variant="h5" gutterBottom>
            <strong>FAQ: Service Providers</strong>
          </Typography>
          {serviceProviderFAQs.map((faqItem, index) => {
            const id = `sp-faq-${index}`;
            return (
              <Accordion key={id} id={id} sx={{boxShadow: "none", border:"0"}} onChange={handleAccordionChange(id)}>
                <AccordionSummary sx={{boxShadow: "none", border:"0"}} expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={questionStyle}>
                    {`(${index + 1}) ${faqItem.question}`}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{boxShadow: "none", border:"0"}}>
                  <Typography>{faqItem.answer}</Typography>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>

        {/* FAQ for Clients */}
        <Box sx={{ marginBottom: "200px" }}>
          <Typography variant="h5" gutterBottom>
            <strong>FAQ: Clients</strong>
          </Typography>
          {clientFAQs.map((faqItem, index) => {
            const id = `client-faq-${index}`;
            return (
              <Accordion sx={{boxShadow: "none", border:"0"}} key={id} id={id} onChange={handleAccordionChange(id)}>
                <AccordionSummary sx={{boxShadow: "none", border:"0"}} expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={questionStyle}>
                    {`(${index + 1}) ${faqItem.question}`}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{boxShadow: "none", border:"0"}}>
                  <Typography>{faqItem.answer}</Typography>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

export default faq;
